import { selector, selectorFamily, waitForAll } from 'recoil';
import { compact, uniqWith, findIndex } from 'lodash';
import { getAnalyzedSubjectFromRequestSubject, getRequestSubjectFromAnalysisSubject } from 'venn-utils';
import type { Nominal } from 'venn-utils';
import type { StudioRequestSubject, SubjectWithOptionalFee } from '../types';
import { subjectInputGroups, subjectInputGroupSubjects } from './input-management/subjectInput';
import { benchmarkInputs, benchmarkInputSubject } from './input-management/benchmarkInput';
import { originalAnalysisSubjectQuery, requestSubjects } from './subjects';
import { convertToSubjectWithFee, subjectsAreEqualInclFees } from '../utils';

export type ColorIndex = Nominal<number, 'colorIndex'>;

/**
 * Maps a list of subjects to their colors.
 */
export const colorIndicesForSubjectsState = selectorFamily<ColorIndex[], SubjectWithOptionalFee[]>({
  key: 'colorsForSubjects',
  get:
    (subjects) =>
    ({ get }) => {
      const allSubjects = get(allSubjectsSelector)
        .map(getAnalyzedSubjectFromRequestSubject)
        .map((subject) => convertToSubjectWithFee(subject));
      const allUniqSubjects = uniqWith(allSubjects, subjectsAreEqualInclFees);
      return subjects
        .map((subject) => convertToSubjectWithFee(subject))
        .map(
          (subject1) =>
            findIndex(allUniqSubjects, (subject2) => subjectsAreEqualInclFees(subject1, subject2)) as ColorIndex,
        );
    },
});

/**
 * Gets all subjects and commons benchmarks in a view.
 */
export const allSubjectsSelector = selector<StudioRequestSubject[]>({
  key: 'allSubjectsSelector',
  get: ({ get }) => {
    const allSubjectGroups = get(subjectInputGroups);
    const allSubjects = get(waitForAll(allSubjectGroups.map(subjectInputGroupSubjects))).flat();
    const allRequestSubjects = get(requestSubjects(allSubjects));
    const allBenchmarkSubjects = compact(get(waitForAll(get(benchmarkInputs).map(benchmarkInputSubject))));
    const allBenchmarkRequestSubjects = compact(
      get(waitForAll(allBenchmarkSubjects.map(originalAnalysisSubjectQuery))),
    ).map((subject) => getRequestSubjectFromAnalysisSubject(subject));
    return [...allRequestSubjects, ...allBenchmarkRequestSubjects];
  },
});
